import { useContext, useMemo } from 'react';
import cn from 'classnames';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';
import DataTable, { dataTableStyles } from '@creditflowresearch/uilib/Table/DataTable';
import Spinner from '@creditflowresearch/uilib/Loader/Spinner';
import LoadMore from '@/creditflowresearch/ui/components/deals/DealListTable/components/LoadMore/LoadMore';
import CorporateRowWrapper from '@/creditflowresearch/ui/components/deals/DealListTable/components/CorporateRowWrapper';
import { DictionariesContext } from '@/creditflowresearch/application/Dictionaries/DictionariesContext';
import { useDictionariesContext } from '@/creditflowresearch/application/Dictionaries/v2/DictionariesContext';
import { useCorporateMarketPageContext } from '@/creditflowresearch/application/Deal/contexts/IssueTile/CorporateMarketPageContext';
import DateTime, { ITimeZone } from '@dealroadshow/uikit-date-time';
import { useAccessGrant } from '@/creditflowresearch/application/User/access/grants/useAccessGrant';

import styles from '@/creditflowresearch/ui/components/deals/DealListTable/table.module.scss';

interface IMarketDealsTableProps {
  columns: any[],
  showDealDetailsByDefault?: boolean,
  defaultExpandedRowIndexes?: number[],
}

const MarketDealsTable = ({ columns, ...props }: IMarketDealsTableProps) => {
  const {
    collection,
    isFetching,
    loadMore,
    totalCount,
  } = useCorporateMarketPageContext();
  const legacy = useContext(DictionariesContext);
  const v2 = useDictionariesContext();
  const {
    industries,
    subindustries,
    ratings,
    dealStatuses,
    currencies,
    rankings,
    timeZones,
    countries,
    sizeDescriptions,
  } = legacy ?? v2;

  const { checkAccessByUserPlan } = useUserPlanContext();
  const { checkAccessByUserMarket, checkAccessByUserProducts } = usePageFiltersContext();
  const granted = useAccessGrant();
  const checks = [
    granted || checkAccessByUserPlan(UserPlanType.PREMIUM),
    granted || checkAccessByUserMarket(),
    granted || checkAccessByUserProducts(),
  ];

  const mappedCollection = useMemo(() => collection.map((item) => {
    const timeZone = timeZones.getById(item.dates.timeZoneId);
    const sizeDescription = sizeDescriptions.getById(item.sizeDescriptionId);

    return {
      ...item,
      link: item.url,
      leftLeads: item.leads.left,
      activeLeads: item.leads.active,
      jointLeads: item.leads.joint,
      industry: industries.getById(item.industryId),
      subindustry: subindustries.getById(item.subindustryId),
      status: dealStatuses.getById(item.status),
      size: { ...currencies.getById(item.currencyId), size: item.size },
      ratings: item.ratings.map(({ ratingId }) => ratings.getById(ratingId)),
      ranking: rankings.getById(item.rankingId),
      pricingDate: item.dates.pricing
        ? {
            dateTime: new DateTime(item.dates.pricing, { timeZone: timeZone as unknown as ITimeZone }),
            timezone: timeZone,
          }
        : null,
      announcedDate: item.dates.announced
        ? {
            dateTime: new DateTime(item.dates.announced, { timeZone: timeZone as unknown as ITimeZone }),
            timezone: timeZone,
          }
        : null,
      lastUpdatedDate: item.dates.lastUpdated
        ? {
            dateTime: new DateTime(item.dates.lastUpdated, { timeZone: timeZone as unknown as ITimeZone }),
            timezone: timeZone,
          }
        : null,
      maturityDate: item.dates.maturity
        ? {
            dateTime: new DateTime(item.dates.maturity, { timeZone: timeZone as unknown as ITimeZone }),
            timezone: timeZone,
          }
        : null,
      country: countries.getById(item.countryId),
      sizeDescription,
    };
  }), [
    collection,
    industries,
    subindustries,
    ratings,
    dealStatuses,
    currencies,
    rankings,
    timeZones,
    countries,
    sizeDescriptions,
  ]);

  return (
    <>
      <DataTable
        columns={ columns }
        data={ mappedCollection }
        className={ cn(styles.dealsListTable, dataTableStyles.isHoverable) }
        isFetching={ isFetching }
        loadingComponent={ Spinner }
        dataTest="dealsList"
        rowWrapper={ (Row) => (props) => <CorporateRowWrapper Row={ Row } checks={ checks } { ...props } /> }
        { ...props }
      />
      <LoadMore collection={ collection } totalCount={ totalCount } loadMore={ loadMore } />
    </>
  );
};

export default MarketDealsTable;
